import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { loginSchema, validateForm } from "../../validations/authValidations";
import { FiMail, FiLock, FiEye, FiEyeOff } from "react-icons/fi";
import { RiShieldCheckFill } from "react-icons/ri";
import logo from "../../assets/image/logo.svg";
import loginImage from "../../assets/image/logo_shape.png";
import secureLine from "../../assets/image/logo-lines.svg";
import { ArrowRightIcon } from "lucide-react";

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, userRole, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole) {
      // Redirect based on user role
      switch (userRole) {
        case "buyer":
          navigate("/buyer");
          break;
        case "seller":
          navigate("/seller");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "super_admin":
          navigate("/super-admin");
          break;
        default:
          navigate("/login");
      }
    }
  }, [isAuthenticated, userRole, isLoading, navigate]);

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Don't render login form if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form using Yup schema
    const validation = await validateForm(loginSchema, formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Use auth context login function
      const result = await login(formData);

      if (result.success) {
        // Login successful - auth context will handle redirect
        console.log("Login successful");
      } else {
        // Handle login errors
        setErrors({ api: result.error });
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = "Network error. Please try again.";
      setErrors({ api: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  return (
    <div className="h-screen overflow-hidden flex font-inter">
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-end"
        style={{
          background: "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
        }}
      >
        <img
          src={secureLine}
          alt="ProtectPay"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <img
          src={loginImage}
          alt="ProtectPay"
          className="absolute top-5 inset-0 w-full h-full object-contain z-0"
        />
        <div className="relative max-w-[347px] mx-auto z-10 text-center pb-4 px-8">
          <h5 className="text-white font-normal text-xl font-rubik">
            Secure and accountable transactions.
          </h5>
          <p className="text-white font-[400] text-sm font-inter mt-2">
            Keep track of your sales and clients, in an all-in-one platform.
          </p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 bg-white flex flex-col h-screen justify-between">
        <div className="absolute top-0 z-[0] left-0 w-full h-20 bg-blue-500 block lg:hidden"></div>
        <div className="flex-1 flex z-[1] items-start mt-[51px] md:mt-0 md:items-center justify-center">
          <div className="w-full max-w-full sm:max-w-lg md:max-w-lg 2xl:max-w-2xl">
            <div
              className="bg-white p-5 md:p-8 border-[0px] sm:border-[1px] sm:border-[#00000026]"
              style={{ borderRadius: "24px" }}
            >
              <Link to="/" className="flex items-center gap-2">
                <img
                  src={logo}
                  alt="ProtectPay"
                  className="items-center justify-center text-white w-[195px]"
                />
              </Link>

              <div className="mt-8 2xl:mt-16">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 font-rubik">
                  Hi, welcome to ProtectPay!
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  Please enter your credentials to access your account.
                </p>
              </div>

              {errors.api && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {errors.api}
                </div>
              )}

              <form className="mt-8 2xl:mt-16" onSubmit={handleSubmit}>
                <div className="space-y-3 2xl:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiMail className="h-5 w-5" />
                      </div>
                      <input
                        name="email"
                        type="email"
                        required
                        className={`block w-full rounded-lg border ${
                          errors.email ? "border-red-300" : "border-gray-300"
                        } bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="alexandre@gmail.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                    </div>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiLock className="h-5 w-5" />
                      </div>
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        className={`block w-full rounded-lg border ${
                          errors.password ? "border-red-300" : "border-gray-300"
                        } bg-white py-2.5 pl-10 pr-10 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <FiEyeOff className="h-5 w-5" />
                        ) : (
                          <FiEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.password}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm w-full text-right my-4 2xl:my-8 text-indigo-600 hover:text-indigo-700"
                >
                  Forgot your password?
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-0 w-full rounded-lg bg-indigo-600 py-2.5 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Logging in…" : "Login"}
                </button>

                <div className="mt-4 flex items-center justify-center gap-4 text-center text-[12px] md:text-[14px] text-gray-600">
                  <div>Not registered yet? </div>
                  <div className="flex items-center gap-1">
                    <a
                      href="/register"
                      className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Create an account now
                    </a>
                    <ArrowRightIcon className="w-4 h-4" />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <footer className="hidden mb-1 lg:block px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-gray-500">
            <p className="text-[12px]">
              © 2025 ProtectPay. All rights reserved. Licensed escrow service
              provider.
            </p>
            <div className="flex items-center gap-2 text-[12px]">
              <span>Secured by</span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/60 text-indigo-600">
                <RiShieldCheckFill className="h-4 w-4" />
              </span>
              <span>Escrow level type security</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
